import { useContext, useId, useState, useCallback } from 'react';
import { Select as BaseSelect } from '@base-ui-components/react/select';
import { componentZIndex } from '../../lib/zIndexLayers';
import {
  cn,
  gaiaTheme,
  getFieldHelperTextClasses,
  getFieldLabelClasses,
  getFieldRequiredClasses,
} from './baseUIConfig';
import { ModalContext } from './Modal';

export const Select = ({
  value,
  onChange,
  options,
  placeholder = 'Select an option',
  className = '',
  disabled = false,
  error = false,
  helperText,
  id,
  label,
  required = false,
  isInModal = false,
  isInPopup = false,
  forceNative = false,
  showOptionDescriptions = false,
  maxVisibleItems = null,
}) => {
  const generatedId = useId();
  const selectId = id || generatedId;
  const errorMessage = typeof error === 'string' ? error : '';
  const hasError = Boolean(error);
  const helperId = helperText && !hasError ? `${selectId}-helper` : undefined;
  const errorId = hasError && errorMessage ? `${selectId}-error` : undefined;
  const modalContext = useContext(ModalContext);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const handleScroll = useCallback((e) => {
    const el = e.target;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
    setIsAtBottom(atBottom);
  }, []);

  const portalContainer = modalContext?.portalContainer || null;
  const items = Array.isArray(options)
    ? options.map((opt) => ({
      value: opt?.value ?? '',
      label: opt?.label ?? String(opt?.value ?? ''),
    }))
    : undefined;
  const selectedLabel = items?.find((item) => item.value === value)?.label;
  const shouldDisableModal = isInModal || isInPopup || Boolean(portalContainer);
  const dropdownZIndex = isInPopup
    ? componentZIndex.selectDropdownInPopup
    : isInModal
      ? componentZIndex.selectDropdownInModal
      : componentZIndex.selectDropdownDefault;
  const describedBy = errorId || helperId;

  const triggerClasses = cn(
    gaiaTheme.components.select.trigger,
    hasError && 'border-gaia-danger focus:border-gaia-danger focus:ring-gaia-danger/20',
    className
  );

  const handleValueChange = (next) => {
    let normalized;
    if (typeof next === 'string') {
      normalized = next;
    } else if (next && typeof next === 'object') {
      normalized = next.value ?? next.target?.value ?? '';
    } else {
      normalized = String(next ?? '');
    }
    if (onChange) onChange(normalized);
  };

  return (
    <div className={gaiaTheme.components.field.container}>
      {label && (
        <label htmlFor={selectId} className={getFieldLabelClasses()}>
          {label}
          {required && <span className={getFieldRequiredClasses()}>*</span>}
        </label>
      )}

      {forceNative ? (
        <select
          id={selectId}
          className={triggerClasses}
          value={value ?? ''}
          onChange={(e) => handleValueChange(e)}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={hasError ? 'true' : 'false'}
        >
          {Array.isArray(options) && options.map((opt) => (
            <option key={opt.value} value={opt.value} title={opt.description || undefined}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <BaseSelect.Root
          value={value ?? ''}
          onValueChange={handleValueChange}
          disabled={disabled}
          modal={shouldDisableModal ? false : undefined}
          items={items}
        >
          <BaseSelect.Trigger
            id={selectId}
            className={triggerClasses}
            aria-describedby={describedBy}
            aria-invalid={hasError ? 'true' : 'false'}
          >
            <span className="mr-2 truncate">
              {selectedLabel || <BaseSelect.Value placeholder={placeholder} />}
            </span>
            <BaseSelect.Icon>
              <svg
                className="h-4 w-4 transition-transform ui-expanded:rotate-180"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </BaseSelect.Icon>
          </BaseSelect.Trigger>

          <BaseSelect.Portal container={portalContainer ?? undefined}>
            <BaseSelect.Positioner
              align="start"
              alignItemWithTrigger={false}
              sideOffset={6}
              collisionPadding={12}
              collisionBoundary={portalContainer ?? undefined}
            >
              <BaseSelect.Popup
                className={cn(dropdownZIndex, gaiaTheme.components.select.menu)}
                style={{
                  maxHeight: maxVisibleItems
                    ? `${maxVisibleItems * (showOptionDescriptions ? 52 : 40)}px`
                    : '60vh',
                }}
                onScroll={handleScroll}
              >
                <BaseSelect.ScrollUpArrow
                  className={cn(
                    gaiaTheme.components.select.scrollIndicator,
                    gaiaTheme.components.select.scrollIndicatorTop
                  )}
                >
                  <svg
                    className="h-4 w-4 animate-bounce"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 14l-6-6-6 6" />
                  </svg>
                </BaseSelect.ScrollUpArrow>
                {Array.isArray(options) &&
                  options.map((option) => (
                    <BaseSelect.Item
                      key={option.value}
                      value={option.value}
                      title={option.description || undefined}
                      className={gaiaTheme.components.select.item}
                    >
                      <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
                      {showOptionDescriptions && option.description && (
                        <span className={gaiaTheme.components.select.optionDescription}>
                          {option.description}
                        </span>
                      )}
                    </BaseSelect.Item>
                  ))}
                {maxVisibleItems && Array.isArray(options) && options.length > maxVisibleItems && !isAtBottom && (
                  <div className="sticky bottom-0 left-0 right-0 pointer-events-none z-20">
                    <div className={gaiaTheme.components.select.scrollMore}>
                      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      <span>Scroll for more</span>
                    </div>
                  </div>
                )}
                <BaseSelect.ScrollDownArrow
                  className={cn(
                    gaiaTheme.components.select.scrollIndicator,
                    gaiaTheme.components.select.scrollIndicatorBottom
                  )}
                >
                  <svg
                    className="h-4 w-4 animate-bounce"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 10l6 6 6-6" />
                  </svg>
                </BaseSelect.ScrollDownArrow>
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
      )}

      {errorId && (
        <p id={errorId} className={getFieldHelperTextClasses(true)}>
          {errorMessage}
        </p>
      )}

      {helperId && (
        <p id={helperId} className={getFieldHelperTextClasses(false)}>
          {helperText}
        </p>
      )}
    </div>
  );
};

export const SelectOption = ({ value, label }) => ({ value, label });
